import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "../lib/api";
import { Bell } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { toast } from "sonner";

export function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  const wsRef = useRef();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items);
      setUnread(data.unread);
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    if ("Notification" in window && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch {}
    }
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const base = BACKEND_URL.replace(/^http/, "ws");
        const ws = new WebSocket(`${base}/api/ws/notifications?token=${token}`);
        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            if (msg.type === "notification") {
              setItems((p) => [msg.notification, ...p]);
              setUnread((u) => u + 1);
              toast(msg.notification.title, { description: msg.notification.body });
              try {
                if (window.AndroidBridge && window.AndroidBridge.showNotification) {
                  window.AndroidBridge.showNotification(msg.notification.title, msg.notification.body || "", msg.notification.link || "/");
                }
              } catch {}
              if ("Notification" in window && Notification.permission === "granted") {
                try {
                  const n = new Notification(msg.notification.title, { body: msg.notification.body || "", icon: "/logo.png" });
                  n.onclick = () => { window.focus(); if (msg.notification.link) window.location.href = msg.notification.link; };
                } catch {}
              }
            }
          } catch {}
        };
        wsRef.current = ws;
      } catch {}
    }
    return () => {
      clearInterval(t);
      try { wsRef.current?.close(); } catch {}
    };
  }, []);

  const onOpenChange = async (open) => {
    if (open && unread > 0) {
      try {
        await api.post("/notifications/read");
        setUnread(0);
        setItems((p) => p.map((n) => ({ ...n, read: true })));
      } catch {}
    }
  };

  const click = (n) => { if (n.link) navigate(n.link); };

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger data-testid="notif-bell" className="relative text-gray-300 hover:text-white outline-none">
        <Bell size={22} />
        {unread > 0 && (
          <span data-testid="notif-unread-badge" className="absolute -top-1.5 -right-1.5 bg-[#E50914] text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent data-testid="notif-panel" align="end" className="bg-[#1a1a1a] border-white/10 text-white w-80 max-h-[26rem] overflow-y-auto p-0">
        <div className="px-3 py-2.5 border-b border-white/10 font-semibold text-sm sticky top-0 bg-[#1a1a1a]">Bildirimler</div>
        {items.length === 0 ? (
          <div className="px-3 py-8 text-center text-sm text-gray-500">Henüz bildirim yok</div>
        ) : (
          items.map((n) => (
            <button key={n.id} data-testid="notif-item" onClick={() => click(n)}
              className={`w-full text-left px-3 py-2.5 border-b border-white/5 hover:bg-white/5 transition ${!n.read ? "bg-white/[0.04]" : ""}`}>
              <div className="text-sm font-medium flex items-center gap-1.5">
                {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#E50914] shrink-0" />}{n.title}
              </div>
              {n.body && <div className="text-xs text-gray-400 mt-0.5 line-clamp-2">{n.body}</div>}
            </button>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
